import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { dateTimeParts } from '../format-date';
import { holds, useExpensesPermissions } from '../use-expenses-permissions';
import type { ReportDecision, RowDecision } from './api-decisions';
import { DecisionPanel } from './DecisionPanel';
import { DecisionSheet } from './DecisionSheet';
import { PHONE_BOTTOM_SHEET } from './phone-sheet';
import { ReportStatusBadge } from './ReportStatusBadge';
import { useShellWide } from './use-shell-wide';
import type { DecisionError } from './use-decisions';
import type { Report, ReportExpenseRow } from './types';

export const EXPENSES_APPROVE = 'expenses:report:approve';

/**
 * §1.5: the first decision, above the table, while the report waits at the 2nd level: who
 * approved, when, and the note when there is one (`firstApproval`); `firstApprover` names them
 * when an older server sends no `firstApproval`.
 */
export function FirstDecision({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  if (report.status !== 'submitted' || report.approvalStage !== 2) return null;
  const approval = report.firstApproval;
  const by = approval?.by ?? report.firstApprover;
  if (!by) return null;
  const when = approval?.at ? dateTimeParts(approval.at) : null;
  const note = approval?.note?.trim();
  const key = note ? 'firstLevelOnWith' : 'firstLevelOn';
  const text = when
    ? t(`reports.page.decision.${key}`, {
        name: by.displayName,
        date: when.date,
        time: when.time,
        note,
      })
    : t('reports.page.decision.firstLevel', { name: by.displayName });
  return (
    <p
      className="border-border bg-muted/50 rounded-lg border p-3 text-sm"
      data-testid="first-decision"
    >
      {text}
    </p>
  );
}

/**
 * The line of §1.4 for someone who may approve but whose own report this is: at any level, while
 * the report waits. The API says it with `can.owned`.
 */
export function OwnReportLine({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  const permissions = useExpensesPermissions();
  if (
    report.status !== 'submitted' ||
    report.can?.owned !== true ||
    !holds(permissions, EXPENSES_APPROVE)
  ) {
    return null;
  }
  return (
    <p role="status" className="text-muted-foreground text-sm" data-testid="own-report-line">
      {t('reports.page.decision.ownReport')}
    </p>
  );
}

export function DecisionMessages({
  error,
  done,
}: {
  readonly error: DecisionError | null;
  readonly done: ReportDecision | null;
}) {
  const { t } = useTranslation('expenses');
  if (error) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {t(`reports.page.decision.errors.${error}`)}
      </p>
    );
  }
  if (!done) return null;
  return (
    <p role="status" className="text-sm font-semibold">
      {t(`reports.page.decision.done.${done}`)}
    </p>
  );
}

type RowAsk = { readonly row: ReportExpenseRow; readonly kind: RowDecision };

/** The note a row's «Rifiuta» or «Rendi rimborsabile…» asks for, then the decision. */
export function useRowAsk(
  busy: boolean,
  onDecide: (kind: RowDecision, expenseId: string, note: string) => Promise<unknown>,
) {
  const { t } = useTranslation('expenses');
  const [ask, setAsk] = useState<RowAsk | null>(null);
  const sheet = ask ? (
    <DecisionSheet
      title={t(`reports.page.decision.${ask.kind === 'reject' ? 'rejectTitle' : 'makeTitle'}`)}
      help={t(`reports.page.decision.${ask.kind === 'reject' ? 'rejectHelp' : 'makeHelp'}`)}
      confirmLabel={t(
        `reports.page.decision.${ask.kind === 'reject' ? 'rejectExpense' : 'makeReimbursable'}`,
      )}
      destructive={ask.kind === 'reject'}
      busy={busy}
      onClose={() => setAsk(null)}
      onConfirm={(note) => void onDecide(ask.kind, ask.row.id, note).then(() => setAsk(null))}
    />
  ) : null;
  return { sheet, open: (row: ReportExpenseRow, kind: RowDecision) => setAsk({ row, kind }) };
}

/** True when the caller may take one of the note's three decisions. */
export function canDecide(report: Report): boolean {
  const can = report.can ?? {};
  return can.approve === true || can.sendBack === true || can.refuse === true;
}

export const DECISION_ID = 'report-decision';

/**
 * «Decidi» in the header bar from 620 px of shell width (#952 H5): the bar stays at the top while
 * the page scrolls, and this brings the decision back into view with the focus in its note.
 */
export function DecideButton({ report }: { readonly report: Report }) {
  const { t } = useTranslation('expenses');
  const wide = useShellWide();
  if (!wide || !canDecide(report)) return null;
  return (
    <Button
      type="button"
      onClick={() => {
        const panel = document.getElementById(DECISION_ID);
        panel?.scrollIntoView?.({ block: 'start' });
        (panel?.querySelector('textarea') ?? panel)?.focus({ preventScroll: true });
      }}
    >
      {t('reports.page.decision.open')}
    </Button>
  );
}

/**
 * The approver's decision (spec §1.4, #952 H5): from 620 px of shell width the panel right under the
 * header, before the expenses; on a phone a bar «Decidi» at the bottom of the screen that opens it
 * in a bottom sheet. Someone who may approve but owns the note reads why they cannot.
 */
export function DecisionArea({
  report,
  busy,
  error,
  onDecide,
}: {
  readonly report: Report;
  readonly busy: boolean;
  /** The last decision's failure, repeated inside the phone's sheet, which covers the page. */
  readonly error: DecisionError | null;
  readonly onDecide: (kind: ReportDecision, note: string) => Promise<boolean>;
}) {
  const { t } = useTranslation('expenses');
  const { t: tc } = useTranslation('common');
  const [sheet, setSheet] = useState(false);
  const wide = useShellWide();
  if (!canDecide(report)) return <OwnReportLine report={report} />;
  // One panel only, chosen by width: hiding one copy with CSS left its ids twice in the document.
  if (wide) {
    return (
      <div id={DECISION_ID} tabIndex={-1} className="scroll-mt-56 outline-none">
        <DecisionPanel report={report} busy={busy} onDecide={onDecide} />
      </div>
    );
  }
  return (
    <>
      <div
        role="group"
        aria-label={t('reports.page.decision.barLabel')}
        className="bulk-bar order-last"
        data-testid="decision-bar"
      >
        <ReportStatusBadge status={report.status} stage={report.approvalStage} />
        <div className="bulk-bar-actions">
          <Button type="button" className="min-h-11" disabled={busy} onClick={() => setSheet(true)}>
            {t('reports.page.decision.open')}
          </Button>
        </div>
      </div>
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="bottom" className={`${PHONE_BOTTOM_SHEET} flex flex-col gap-3 p-4`}>
          <div className="flex items-center justify-between gap-2">
            <SheetTitle>
              {t('reports.page.decision.sheetTitle', { number: report.number })}
            </SheetTitle>
            <SheetClose asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-mr-2"
                aria-label={tc('list.close')}
              >
                <X aria-hidden="true" />
              </Button>
            </SheetClose>
          </div>
          <SheetDescription className="sr-only">
            {t('reports.page.decision.noteRequired')}
          </SheetDescription>
          <DecisionMessages error={error} done={null} />
          <DecisionPanel
            report={report}
            busy={busy}
            onDecide={async (kind, note) => {
              // Closed only once the decision is taken: a refusal keeps the sheet and the note.
              const taken = await onDecide(kind, note);
              if (taken) setSheet(false);
              return taken;
            }}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
